'use client';

import React, { memo, useCallback, useContext, useMemo } from "react";
import { appContext } from "../../App";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faRocket } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { CryptoIconsRow } from "./CryptoIconsRow";
import { useNavigate } from "react-router-dom";

interface HomeComponentProps {
  contactRef: React.RefObject<HTMLDivElement>;
}


const PremiumBackground = memo(() => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient Mesh Background */}
      <div className="absolute inset-0"
        style={{
          background: "radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(37, 99, 235, 0.05) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(30, 64, 175, 0.03) 0%, transparent 50%)"
        }}
      />

      {/* Glow Effects */}
      <div className="absolute top-1/4 left-1/4 w-[200px] h-[200px] md:w-[600px] md:h-[600px]">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/3 to-cyan-500/2 blur-xl md:blur-3xl rounded-full" />
      </div>
      
      <div className="absolute bottom-1/4 right-1/4 w-[250px] h-[250px] md:w-[700px] md:h-[700px]">
        <div className="absolute inset-0 bg-gradient-to-l from-indigo-600/3 to-blue-500/2 blur-xl md:blur-3xl rounded-full" />
      </div>
    </div>
  );
});

PremiumBackground.displayName = 'PremiumBackground';

const StatItem = memo(({ value, label, delay }: { value: string; label: string; delay: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="flex-1 min-w-[100px]"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay }}
    >
      {/* Mobile: Centered layout, Desktop: Original flex layout */}
      <div className="flex flex-col items-center text-center md:flex-row md:items-start md:text-left md:gap-4">
        <motion.div
          className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400"
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ duration: 0.3, delay: delay + 0.2, type: "spring" }}
        >
          {value}
        </motion.div>
      <div className="text-xs md:text-sm text-slate-600 dark:text-slate-400 font-medium leading-tight mt-1 md:mt-0">
        <div className="md:hidden">{label}</div>
        <div className="hidden md:block">
          {label.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < label.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>
      </div>
      </div>
    </motion.div>
  );
});

StatItem.displayName = 'StatItem';

const PrimaryButton = memo(({ onClick, children, icon }: { onClick: () => void; children: React.ReactNode; icon: any }) => {
  return (
    <motion.button
      onClick={onClick}
      className="group relative w-full sm:w-auto px-6 md:px-10 py-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 
               text-white font-semibold text-sm md:text-base shadow-lg md:shadow-2xl shadow-blue-500/15 md:shadow-blue-500/20 
               overflow-hidden active:scale-95 touch-manipulation"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="relative flex items-center justify-center gap-2">
        <span className="font-semibold truncate">{children}</span>
        <FontAwesomeIcon icon={icon} className="w-3 h-3 md:w-4 md:h-4" />
      </div>
    </motion.button>
  );
});

PrimaryButton.displayName = 'PrimaryButton';

const SecondaryButton = memo(({ onClick, children, icon }: { onClick: () => void; children: React.ReactNode; icon: any }) => {
  return (
    <motion.button
      onClick={onClick}
      className="group relative w-full sm:w-auto px-6 md:px-10 py-4 rounded-xl bg-white/90 dark:bg-slate-800/60 backdrop-blur-sm 
               border border-slate-300/40 dark:border-slate-700/40 text-slate-800 dark:text-slate-200 
               font-semibold text-sm md:text-base overflow-hidden active:scale-95 touch-manipulation"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <div className="relative flex items-center justify-center gap-2">
        <FontAwesomeIcon icon={icon} className="w-3 h-3 md:w-4 md:h-4" />
        <span className="truncate">{children}</span>
      </div>
    </motion.button>
  );
});

SecondaryButton.displayName = 'SecondaryButton';

const HeadingText = memo(() => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  return (
    <motion.div
      ref={ref}
      className="mb-6 md:mb-14 text-center md:text-left"
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : {}}
      transition={{ duration: 0.5 }}
    >
      <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-light mb-3 md:mb-6 leading-tight md:leading-[0.9] tracking-tight">
        <div className="block text-slate-900 dark:text-white">
          POWERING
        </div>
        <div className="relative inline-block mx-auto md:mx-0">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-500 block">
            CRYPTO MINING
          </span>
        </div>
        <div className="block text-slate-900 dark:text-white">
          AT SCALE
        </div>
      </h1>
    </motion.div>
  );
});

HeadingText.displayName = 'HeadingText';

export const HomeComponent: React.FC<HomeComponentProps> = ({ contactRef}) => {
  const { darkMode } = useContext(appContext);
  const navigate= useNavigate()

  const handleContactClick = useCallback(() => {
    contactRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  }, [contactRef]);

  const handleDashboardClick = useCallback(() => {
    navigate('/login');
  }, []);

  const stats = useMemo(() => [
    { value: "542.8 PH/s", label: "Total\nHashrate" },
    { value: "1.66 BTC", label: "Weekly BTC\nMined" },
    { value: "6000+", label: "Active\nMiners" },
  ], []);

  return (
    <motion.div 
      className="relative min-h-screen flex items-center pt-16 md:pt-20 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-slate-900 dark:via-slate-900 dark:to-blue-900/10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <PremiumBackground />
      

      <div className="container mx-auto px-4 sm:px-5 lg:px-8 relative z-10">
        <div className="flex flex-col items-start md:items-center justify-center">
          <div className="w-full max-w-4xl">
            <HeadingText />

            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:flex md:flex-wrap md:items-center md:gap-6 md:mb-12 mb-8">
            {stats.map((stat, index) => (
              <React.Fragment key={index}>
                <div className="md:contents">
                  {/* Mobile: Card layout with centered text */}
                  <div className="md:hidden bg-white dark:bg-slate-800/50 rounded-lg p-4 shadow-sm border border-slate-200/50 dark:border-slate-700/50">
                    <StatItem 
                      value={stat.value} 
                      label={stat.label} 
                      delay={index * 0.1} 
                    />
                  </div>
                  
                  {/* Desktop: Original inline layout */}
                  <div className="hidden md:block">
                    <StatItem 
                      value={stat.value} 
                      label={stat.label} 
                      delay={index * 0.1} 
                    />
                  </div>
                </div>
                
                {index < stats.length - 1 && (
                  <div className="hidden md:block w-px h-8 bg-gradient-to-b from-transparent via-slate-300/50 to-transparent dark:via-slate-700/50" />
                )}
              </React.Fragment>
            ))}
          </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-start mb-8 md:mb-16">
              <PrimaryButton onClick={handleContactClick} icon={faRocket}>
                Start Mining
              </PrimaryButton>
              
              <SecondaryButton onClick={handleDashboardClick} icon={faChartLine}>
                View Dashboard
              </SecondaryButton>
            </div>
          </div>
        </div>
        {/* <CryptoIconsRow/> */}
      </div>
    </motion.div>
  );
};